import { ButtonBase } from '@mui/material';
import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useState } from 'react';
import {
    Box,
    Button,
    Divider,
    Dropdown,
    IconButton,
    Paper,
    RichTextArea,
    Stack,
    Text,
    Tooltip,
    ViewMore,
    styled,
} from 'shell/components';
import { getTemplatedFeedback, getTooltipComponent, stripHTML } from 'utils/helper';

const FeedbackRenderer = ({ currentSubmission, form, setForm, selectedFeedbackId, setSelectedFeedbackId }) => {
    const { currentAssignment } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const { joyrideRun } = useJoyride();
    const { message } = useSnackbar();

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(form.feedback.commentary);

    const handleClickEdit = () => {
        setIsEditing(true);
        setValue(form.feedback.commentary);
    };

    const handleClickCopy = async (string) => {
        await navigator.clipboard.writeText(stripHTML(string));
        message.success('Copied to clipboard.');
    };

    const handleClickDelete = () => {
        setDialog({
            open: true,
            message: 'Delete Feedback?',
            description: 'Are you sure you want to delete this feedback? This can’t be undone.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            onAction: () => {
                setForm((current) => ({ ...current, feedback: { ...current.feedback, commentary: '' } }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickSave = () => {
        setForm((current) => ({ ...current, feedback: { ...current.feedback, commentary: value } }));
        setIsEditing(false);
    };

    const handleClickCancel = () => {
        setIsEditing(false);
        setValue(form.feedback.commentary);
    };

    const handleClickCopyAll = async () => {
        const str = form.feedback.improvement_suggestions
            .filter(({ active }) => active)
            .reduce((acc, cur, index) => {
                return !index ? `${acc}${stripHTML(cur.text)}` : `${acc}\n\n${stripHTML(cur.text)}`;
            }, '');
        await navigator.clipboard.writeText(str);
        message.success('Copied to clipboard.');
    };

    const handleClickDeleteAll = async () => {
        setDialog({
            open: true,
            message: 'Delete All Detailed Feedback?',
            description: 'Are you sure you want to delete all detailed feedback? This can’t be undone.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            onAction: () => {
                setForm((current) => ({
                    ...current,
                    feedback: {
                        ...current.feedback,
                        improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                            ...item,
                            active: false,
                        })),
                    },
                }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    if (currentSubmission.ai.feedback.feedback_type === 'rubric') {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        Assignment Feedback
                    </Text>
                </BackgroundStack>
                <Stack
                    sx={(theme) => ({
                        flex: 1,
                        p: 3,
                        background: theme.palette.ensightful.neutral[300],
                    })}
                    spacing={2.5}
                >
                    {form.feedback.improvement_suggestions
                        .filter(({ active }) => active)
                        .map((item) => (
                            <CriterionFeedback
                                key={item.id}
                                feedback={item}
                                setForm={setForm}
                                selectedFeedbackId={selectedFeedbackId}
                                setSelectedFeedbackId={setSelectedFeedbackId}
                            />
                        ))}
                </Stack>
            </>
        );
    }

    if (currentSubmission.ai.feedback.feedback_type === 'templated') {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            Assignment Feedback
                        </Text>
                        <Tooltip
                            content={
                                'This assignment is using Smart Feedback Templates. When you make a grading selection, the feedback template for that grade will appear here.'
                            }
                            type={'info'}
                        />
                    </Stack>
                    <Stack direction={'row'} spacing={2}>
                        <IconButton
                            aria-label={'copy feedback'}
                            disabled={!Object.keys(form.criteria).length}
                            kind={'copy'}
                            size={'small'}
                            tooltip={{ component: 'Copy' }}
                            variant={'square'}
                            onClick={() => handleClickCopy(getTemplatedFeedback(currentAssignment, form))}
                        />
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ flex: 1, position: 'relative', p: 3 }}>
                    {Object.keys(form.criteria).length ? (
                        <Text rich>{getTemplatedFeedback(currentAssignment, form)}</Text>
                    ) : (
                        <CenterText align={'center'} color={'light'}>
                            This assignment is using Smart Feedback Templates. Feedback will appear here as you make
                            grading selections.
                        </CenterText>
                    )}
                </Stack>
            </>
        );
    }

    return (
        <>
            <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text variant={'p1'} weight={'semibold'}>
                        General Feedback
                    </Text>
                    <Tooltip
                        content={getTooltipComponent(
                            'General Feedback',
                            'This section provides a broad overview, highlighting key strengths and areas for improvement across student work.'
                        )}
                        type={'info'}
                    />
                </Stack>
                <Stack direction={'row'} spacing={2}>
                    <IconButton
                        aria-label={form.feedback.commentary === '' ? 'add feedback' : 'edit feedback'}
                        disabled={joyrideRun}
                        kind={form.feedback.commentary === '' ? 'add' : 'edit'}
                        size={'small'}
                        tooltip={{ component: form.feedback.commentary === '' ? 'Add' : 'Edit' }}
                        variant={'square'}
                        onClick={handleClickEdit}
                    />
                    <IconButton
                        aria-label={'copy feedback'}
                        disabled={form.feedback.commentary === ''}
                        kind={'copy'}
                        size={'small'}
                        tooltip={{ component: 'Copy' }}
                        variant={'square'}
                        onClick={() => handleClickCopy(form.feedback.commentary)}
                    />
                    <IconButton
                        aria-label={'delete feedback'}
                        disabled={form.feedback.commentary === '' || joyrideRun}
                        kind={'delete'}
                        size={'small'}
                        tooltip={{ component: 'Delete' }}
                        variant={'square'}
                        onClick={handleClickDelete}
                    />
                </Stack>
            </BackgroundStack>
            <Stack sx={{ p: 3 }}>
                {isEditing ? (
                    <Stack spacing={3}>
                        <RichTextArea placeholder={'Write your feedback here'} value={value} onChange={setValue} />
                        <Stack direction={'row-reverse'} spacing={3}>
                            <Button onClick={handleClickSave}>Save</Button>
                            <Button color={'neutral'} variant={'tertiary'} onClick={handleClickCancel}>
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <>
                        {!form.feedback.commentary ? (
                            <Text sx={{ my: 4 }} align={'center'} color={'light'}>
                                This feedback was deleted
                            </Text>
                        ) : (
                            <Text rich>{form.feedback.commentary}</Text>
                        )}
                    </>
                )}
            </Stack>
            {currentSubmission.ai.feedback.feedback_type !== 'custom' && (
                <>
                    <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Text variant={'p1'} weight={'semibold'}>
                                Detailed Feedback
                            </Text>
                            <Tooltip
                                content={getTooltipComponent(
                                    'Detailed Feedback',
                                    'Here, specific feedback is provided with highlighted examples. Students can use these examples to help them identify other areas where they can improve.'
                                )}
                                type={'info'}
                            />
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            <IconButton
                                aria-label={'copy feedback'}
                                disabled={form.feedback.improvement_suggestions.every(({ active }) => !active)}
                                kind={'copy'}
                                size={'small'}
                                tooltip={{ component: 'Copy' }}
                                variant={'square'}
                                onClick={handleClickCopyAll}
                            />
                            <IconButton
                                aria-label={'delete feedback'}
                                disabled={
                                    form.feedback.improvement_suggestions.every(({ active }) => !active) || joyrideRun
                                }
                                kind={'delete'}
                                size={'small'}
                                tooltip={{ component: 'Delete' }}
                                variant={'square'}
                                onClick={handleClickDeleteAll}
                            />
                        </Stack>
                    </BackgroundStack>
                    <Stack
                        sx={(theme) => ({
                            position: 'relative',
                            flex: 1,
                            p: 3,
                            background: theme.palette.ensightful.neutral[300],
                        })}
                        spacing={2.5}
                    >
                        {!form.feedback.improvement_suggestions.filter(({ active }) => active).length ? (
                            <Text
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                }}
                                color={'light'}
                            >
                                This feedback was deleted.
                            </Text>
                        ) : (
                            form.feedback.improvement_suggestions
                                .filter(({ active }) => active)
                                .map((item) => (
                                    <DetailedFeedback
                                        key={item.id}
                                        feedback={item}
                                        setForm={setForm}
                                        selectedFeedbackId={selectedFeedbackId}
                                        setSelectedFeedbackId={setSelectedFeedbackId}
                                    />
                                ))
                        )}
                    </Stack>
                </>
            )}
        </>
    );
};

const CriterionFeedback = ({ feedback, setForm, selectedFeedbackId, setSelectedFeedbackId }) => {
    const { currentAssignment } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();

    const [isEditing, setIsEditing] = useState({ positive: false, negative: false });
    const [value, setValue] = useState({ positive: feedback.positive.text, negative: feedback.negative.text });
    const [isHovered, setIsHovered] = useState(false);

    const handleSelect = async (key, targetItem, feedbackType) => {
        if (key === 'edit') {
            setIsEditing((current) => ({ ...current, [feedbackType]: true }));
            setValue((current) => ({ ...current, [feedbackType]: targetItem[feedbackType].text }));
        }
        if (key === 'copy') {
            await navigator.clipboard.writeText(stripHTML(targetItem[feedbackType].text));
            message.success('Copied to clipboard.');
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: 'Delete Feedback?',
                description: 'Are you sure you want to delete this feedback? This can’t be undone.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        feedback: {
                            ...current.feedback,
                            improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                                return item.id === targetItem.id
                                    ? { ...item, [feedbackType]: { ...item[feedbackType], active: false } }
                                    : item;
                            }),
                        },
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleClickSave = (feedbackType) => {
        setForm((current) => {
            return {
                ...current,
                feedback: {
                    ...current.feedback,
                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                        return item.id === feedback.id
                            ? {
                                  ...item,
                                  [feedbackType]: {
                                      ...item[feedbackType],
                                      active: true,
                                      text: value[feedbackType],
                                  },
                              }
                            : item;
                    }),
                },
            };
        });
        setIsEditing((current) => ({ ...current, [feedbackType]: false }));
    };

    const handleClickCancel = (feedbackType) => {
        setIsEditing((current) => ({ ...current, [feedbackType]: false }));
        setValue((current) => ({ ...current, [feedbackType]: feedback[feedbackType].text }));
    };

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack>
                    <Text variant={'sh2'} weight={'semibold'}>
                        {currentAssignment.rubric.criteria.find(({ id }) => id === feedback.criterion_id).title}
                    </Text>
                    <Divider sx={{ mt: 1.5, mb: 2 }} />
                    <Stack spacing={3}>
                        <Stack sx={{ p: 1 }} spacing={2}>
                            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>What you did well</Text>
                                {!isEditing.positive && (
                                    <>
                                        {feedback.positive.active ? (
                                            <Dropdown
                                                id={`rubric-based-feedback-positive-${feedback.id}`}
                                                options={[
                                                    { key: 'edit', value: 'Edit', icon: 'edit' },
                                                    { key: 'copy', value: 'Copy', icon: 'copy' },
                                                    { key: 'delete', value: 'Delete', icon: 'delete' },
                                                ]}
                                                rootComponent={
                                                    <IconButton
                                                        aria-label={'more options'}
                                                        kind={'moreHorizontal'}
                                                        size={'small'}
                                                        variant={'square'}
                                                    />
                                                }
                                                onSelect={({ key }) => handleSelect(key, feedback, 'positive')}
                                            />
                                        ) : (
                                            <IconButton
                                                kind={'add'}
                                                size={'small'}
                                                tooltip={{ component: 'Add Feedback' }}
                                                variant={'square'}
                                                onClick={() => {
                                                    setIsEditing((current) => ({ ...current, positive: true }));
                                                    setValue((current) => ({ ...current, positive: '' }));
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </Stack>
                            {isEditing.positive ? (
                                <Stack spacing={3}>
                                    <RichTextArea
                                        placeholder={'Highlight what the student excelled at for this criterion.'}
                                        value={value.positive}
                                        onChange={(html) => setValue((current) => ({ ...current, positive: html }))}
                                    />
                                    <Stack direction={'row-reverse'} spacing={3}>
                                        <Button onClick={() => handleClickSave('positive')}>Save</Button>
                                        <Button
                                            color={'neutral'}
                                            variant={'tertiary'}
                                            onClick={() => handleClickCancel('positive')}
                                        >
                                            Cancel
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <>
                                    {feedback.positive.active ? (
                                        <ViewMore rich text={feedback.positive.text} />
                                    ) : (
                                        <Text color={'light'}>This feedback was deleted.</Text>
                                    )}
                                </>
                            )}
                        </Stack>
                        <Stack
                            sx={(theme) => ({
                                borderRadius: '4px',
                                mx: -1,
                                p: 2,
                                background: theme.palette.ensightful.warning[200],
                            })}
                            spacing={2}
                        >
                            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>How you can improve</Text>
                                {!isEditing.negative && (
                                    <>
                                        {feedback.negative.active ? (
                                            <Dropdown
                                                id={`rubric-based-feedback-negative-${feedback.id}`}
                                                options={[
                                                    { key: 'edit', value: 'Edit', icon: 'edit' },
                                                    { key: 'copy', value: 'Copy', icon: 'copy' },
                                                    { key: 'delete', value: 'Delete', icon: 'delete' },
                                                ]}
                                                rootComponent={
                                                    <IconButton
                                                        aria-label={'more options'}
                                                        kind={'moreHorizontal'}
                                                        size={'small'}
                                                        variant={'square'}
                                                    />
                                                }
                                                onSelect={({ key }) => handleSelect(key, feedback, 'negative')}
                                            />
                                        ) : (
                                            <IconButton
                                                kind={'add'}
                                                size={'small'}
                                                tooltip={{ component: 'Add Feedback' }}
                                                variant={'square'}
                                                onClick={() => {
                                                    setIsEditing((current) => ({ ...current, negative: true }));
                                                    setValue((current) => ({ ...current, negative: '' }));
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </Stack>
                            {isEditing.negative ? (
                                <Stack spacing={3}>
                                    <RichTextArea
                                        placeholder={
                                            'Provide constructive feedback on areas where the student can improve for this criterion.'
                                        }
                                        value={value.negative}
                                        onChange={(html) => setValue((current) => ({ ...current, negative: html }))}
                                    />
                                    <Stack direction={'row-reverse'} spacing={3}>
                                        <Button onClick={() => handleClickSave('negative')}>Save</Button>
                                        <Button
                                            color={'neutral'}
                                            variant={'tertiary'}
                                            onClick={() => handleClickCancel('negative')}
                                        >
                                            Cancel
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <>
                                    {feedback.negative.active ? (
                                        <ViewMore rich text={feedback.negative.text} />
                                    ) : (
                                        <Text color={'light'}>This feedback was deleted.</Text>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const DetailedFeedback = ({ feedback, setForm, selectedFeedbackId, setSelectedFeedbackId }) => {
    const { setDialog, closeDialog } = useDialog();
    const { joyrideRun } = useJoyride();
    const { message } = useSnackbar();

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(feedback.text);
    const [isHovered, setIsHovered] = useState(false);

    const handleSelect = async (key, targetItem) => {
        if (key === 'edit') {
            setIsEditing(true);
            setValue(feedback.text);
        }
        if (key === 'copy') {
            await navigator.clipboard.writeText(stripHTML(targetItem.text));
            message.success('Copied to clipboard.');
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: 'Delete Feedback?',
                description: 'Are you sure you want to delete this feedback? This can’t be undone.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        feedback: {
                            ...current.feedback,
                            improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                                return item.id === targetItem.id ? { ...item, active: false } : item;
                            }),
                        },
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleClickSave = () => {
        setForm((current) => ({
            ...current,
            feedback: {
                ...current.feedback,
                improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                    return item.id === feedback.id
                        ? {
                              ...item,
                              text: value,
                          }
                        : item;
                }),
            },
        }));
        setIsEditing(false);
    };

    const handleClickCancel = () => {
        setIsEditing(false);
        setValue(feedback.text);
    };

    return isEditing ? (
        <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
            <Stack spacing={3}>
                <RichTextArea placeholder={'Write your feedback here'} value={value} onChange={setValue} />
                <Stack direction={'row-reverse'} spacing={3}>
                    <Button onClick={handleClickSave}>Save</Button>
                    <Button color={'neutral'} variant={'tertiary'} onClick={handleClickCancel}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </CustomPaper>
    ) : (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'} spacing={2}>
                    <Text align={'left'} rich>
                        {feedback.text}
                    </Text>
                    <Dropdown
                        id={`detailed-feedback-${feedback.id}`}
                        options={[
                            { key: 'edit', value: 'Edit', icon: 'edit', disabled: joyrideRun },
                            { key: 'copy', value: 'Copy', icon: 'copy' },
                            { key: 'delete', value: 'Delete', icon: 'delete', disabled: joyrideRun },
                        ]}
                        rootComponent={
                            <IconButton
                                aria-label={'more options'}
                                kind={'moreHorizontal'}
                                size={'small'}
                                variant={'square'}
                            />
                        }
                        onSelect={({ key }) => handleSelect(key, feedback)}
                    />
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

const CenterText = styled(Text)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '85%',
        transform: 'translate(-50%, -50%)',
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme, isActive }) => {
    return {
        position: 'relative',
        width: '100%',
        padding: theme.spacing(3),
        ...(isActive
            ? {
                  border: `3px solid ${theme.palette.ensightful.warning[700]}`,
              }
            : {
                  border: `3px solid ${theme.palette.ensightful.shades[0]}`,
              }),
    };
});

const LeftBorderHighlight = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '-3px',
        height: 'calc(100% - 12px)',
        width: '3px',
        background: theme.palette.ensightful.warning[700],
        transform: 'translateY(-50%)',
    };
});

export default FeedbackRenderer;
